import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportPaymentDistributionByCustomerCountryData,
  TReportPaymentDistributionByCustomerCountryParams,
} from '@src/types/reports';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import {
  exportPaymentDistributionByCustomerCountryReport,
  getPaymentDistributionByCustomerCountryReport,
} from '@src/services/reports';
import { toast } from 'react-toastify';
import { downloadFile } from '@src/lib/utils';
import { useCountryOptions } from '@src/hooks/useCountryOptions';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';
import { useAuthStore } from '@src/stores/authStore';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

export const PaymentDistributionByCustomerCountry: FC = () => {
  const { auth } = useAuthStore();
  const [data, setData] = useState<
    TReportPaymentDistributionByCustomerCountryData[]
  >([]);
  const { countryOptions } = useCountryOptions();

  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: false,
    enableCountries: true,
  });

  const customColumns = [
    {
      title: 'AMOUNT',
      className: 'text-right',
      render: (item?: TReportPaymentDistributionByCustomerCountryData) => {
        if (!item) return null;

        return <p className="text-white">${item.amount?.toLocaleString()}</p>;
      },
    },
  ];

  const { columns, tableData } =
    useReportTableColumns<TReportPaymentDistributionByCustomerCountryData>({
      data,
      selectedItems: [],
      compareToPreviousPeriod: false,
      compareToSamePeriodPreviousYear: false,
      getAccessField: (item) => item.country,
      getMainDataField: (item) => item.amount,
      customColumns,
      includeTotalsRow: true,
    });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload: TReportPaymentDistributionByCustomerCountryParams = {
        countries:
          filters.selectedCountries.length > 0 ? filters.selectedCountries : [],
      };

      const response =
        await getPaymentDistributionByCustomerCountryReport(payload);

      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const payload: TReportPaymentDistributionByCustomerCountryParams = {
        countries:
          filters.selectedCountries.length > 0 ? filters.selectedCountries : [],
      };
      const response =
        await exportPaymentDistributionByCustomerCountryReport(payload);

      downloadFile(
        response.data,
        'payment-distribution-by-customer-country-report'
      );
      console.log('Exporting report with payload:', payload);
    } catch (error) {
      console.error('Error exporting report:', error);
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Payment Distribution By Customer Country Report
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          showCountriesFilter
          countryOptions={countryOptions}
          isLoading={filters.isLoading}
          selectedCountries={filters.selectedCountries}
          showCompareToPreviousPeriod={false}
          showCompareToSamePeriodPreviousYear={false}
          showGroupByFilter={false}
          showPeriodFilter={false}
          onCountriesChange={filters.setSelectedCountries}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <BarChart data={data}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                angle={-45}
                dataKey="country"
                height={100}
                stroke="#999"
                style={{ fontSize: '12px' }}
                textAnchor="end"
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div
                        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D]"
                        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
                      >
                        <p className="text-white font-semibold mb-1">
                          {payload[0]?.payload?.country}
                        </p>
                        <p className="text-[#B6D72C]">
                          ${payload[0]?.value?.toLocaleString()}
                        </p>
                      </div>
                    );
                  }

                  return null;
                }}
                cursor={{ fill: 'transparent' }}
              />
              <Bar dataKey="amount" fill="#B6D72C" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          {canAccess(
            UserRolePermission.EXPORT_REPORTS,
            auth?.user?.permissions
          ) && (
            <div className="flex justify-end">
              <Button
                className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                variant="outline"
                onClick={handleExportReport}
              >
                Download Excel
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
